import { Color } from "@material-ui/lab";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
	ISocketNotificationItem,
	ISocketNotifications,
	NotificationDeliveryMethodEnum
} from "@remar/shared/dist/models";

import { AppThunk, RootState } from "../../index";
import { usersService } from "../../services";

interface Notification {
	type: Color;
	message: string;
	preventAutoDismiss?: boolean;
}

export interface NotificationItem extends Notification {
	id: number;
}

interface NotificationsState {
	items: NotificationItem[];
	bellNotifications: ISocketNotifications;
	bannerNotifications: ISocketNotifications;
	loadingBellNotifications: boolean;
	loadingBannerNotifications: boolean;
}

const defaultDelay = 3000;

const initialState: NotificationsState = {
	items: [],
	bellNotifications: { items: [] as ISocketNotificationItem[] } as ISocketNotifications,
	bannerNotifications: { items: [] as ISocketNotificationItem[] } as ISocketNotifications,
	loadingBellNotifications: false,
	loadingBannerNotifications: false
};

export const notificationsSlice = createSlice({
	name: "notifications",
	initialState,
	reducers: {
		fireEvent: (state, action: PayloadAction<NotificationItem>) => {
			state.items = [...state.items, action.payload];
		},
		dismiss: (state, action: PayloadAction<number>) => {
			state.items = state.items.filter(item => item.id !== action.payload);
		},
		dismissAll: state => {
			state.items = [];
		},
		pop: state => {
			const newState = state.items.slice();
			newState.shift();
			state.items = newState;
		},
		pushSocketNotifications: (state, { payload }: PayloadAction<ISocketNotificationItem>) => {
			const {
				notification: { deliveryMethod }
			} = payload;
			if (deliveryMethod === NotificationDeliveryMethodEnum.Socket) {
				state.bannerNotifications.items = [...state.bannerNotifications.items, payload];
			} else {
				state.bellNotifications.items = [
					...state.bellNotifications.items,
					payload?.notification ?? ({} as ISocketNotificationItem)
				];
			}
		}
	},
	extraReducers: builder => {
		builder
			.addCase(getUserNotifications.pending, (state, { meta: { arg } }) => {
				const { filters } = arg;
				const type = filters["notification.deliveryMethod"];
				if (type === NotificationDeliveryMethodEnum.Socket) {
					state.loadingBannerNotifications = true;
				} else {
					state.loadingBellNotifications = true;
				}
			})
			.addCase(getUserNotifications.fulfilled, (state, { payload }) => {
				state.bellNotifications = payload;
				state.loadingBellNotifications = true;
			})
			.addCase(getUserNotifications.rejected, (state, { meta: { arg } }) => {
				const { filters } = arg;
				const type = filters["notification.deliveryMethod"];
				if (type === NotificationDeliveryMethodEnum.Socket) {
					state.loadingBannerNotifications = false;
				} else {
					state.loadingBellNotifications = false;
				}
			});
	}
});

export const getUserNotifications = createAsyncThunk(
	"notifications/getUserNotifications",
	async (data: { filters: Record<string, string>; include?: string[] }, { rejectWithValue }) => {
		return await usersService.getUserNotifications(data).catch(e => rejectWithValue(e.message));
	}
);

export const markAsRead = createAsyncThunk("notifications/markAsRead", async (_, { rejectWithValue, dispatch }) => {
	await usersService.markNotificationsAsRead().catch(rejectWithValue);
	dispatch(getUserNotifications({ filters: { "notification.deliveryMethod": NotificationDeliveryMethodEnum.Socket } }));
});

export const { fireEvent, dismiss, pop, dismissAll, pushSocketNotifications } = notificationsSlice.actions;

// DEPRECATED version, for thunks we need to pass only one argument
export const _emit =
	(message: string, color: Color, preventAutoDismiss = false): AppThunk =>
	(dispatch, getState) => {
		const state = getState();
		const lastItem = state.notifications.items[state.notifications.items.length - 1];
		const lastId = lastItem?.id + 1 || 1;
		dispatch(fireEvent({ message, type: color, id: lastId }));
		if (!preventAutoDismiss) {
			setTimeout(() => {
				dispatch(dismiss(lastId));
			}, defaultDelay);
		}
	};

export const emit = createAsyncThunk(
	"notifications/emit",
	(
		{
			message = "Unknown error",
			color,
			preventAutoDismiss = false
		}: {
			message: string;
			color: Color;
			preventAutoDismiss?: boolean;
		},
		{ dispatch, getState }
	) => {
		const state = getState() as RootState;
		const lastItem = state.notifications.items[state.notifications.items.length - 1];
		const lastId = lastItem?.id + 1 || 1;
		dispatch(fireEvent({ message, type: color, id: lastId }));
		if (!preventAutoDismiss) {
			setTimeout(() => {
				dispatch(dismiss(lastId));
			}, defaultDelay);
		}
	}
);

export const getNotificationFullState = (state: RootState): NotificationsState => state.notifications;
export const selectNotifications = (state: RootState): NotificationItem[] => state.notifications.items;

export default notificationsSlice.reducer;
